import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'

pdfMake.addVirtualFileSystem(pdfFonts)

export async function gerarRelatorioComissoes(
  representante,
  comissoes,
  periodo,
) {
  // ─── Paleta de cores ───────────────────────────────────────────────────────
  const COR_PRIMARIA = '#1A237E' // azul escuro
  const COR_SECUNDARIA = '#3949AB' // azul médio
  const COR_ACENTO = '#5C6BC0' // azul claro
  const COR_FUNDO_TH = '#E8EAF6' // lilás bem claro
  const COR_FUNDO_TH2 = '#F3F4FB' // quase branco
  const COR_TEXTO = '#212121'
  const COR_SUBTEXTO = '#616161'
  const COR_BRANCO = '#FFFFFF'
  const COR_VERDE = '#1B5E20'
  const COR_VERDE_BG = '#E8F5E9'

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const fmt = (v) =>
    Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

  const fmtPct = (v) => `${Number(v).toFixed(2).replace('.', ',')}%`

  const fmtData = (d) => {
    if (!d) return ''
    const dt = new Date(d)
    return dt.toLocaleDateString('pt-BR')
  }

  // ─── Totais gerais ─────────────────────────────────────────────────────────
  const totalGeral = comissoes.reduce((s, c) => s + Number(c.total_pedido), 0)
  const comissaoGeral = comissoes.reduce((s, c) => s + Number(c.comissao), 0)
  const pctMedio = totalGeral > 0 ? (comissaoGeral / totalGeral) * 100 : 0

  // ─── Cabeçalho do documento ────────────────────────────────────────────────
  const header = [
    // Barra azul topo
    {
      canvas: [
        {
          type: 'rect',
          x: 0,
          y: 0,
          w: 515,
          h: 6,
          color: COR_PRIMARIA,
          r: 3,
        },
      ],
      margin: [0, 0, 0, 12],
    },
    {
      columns: [
        // Lado esquerdo: título
        {
          stack: [
            {
              text: 'RELATÓRIO DE COMISSÕES',
              fontSize: 18,
              bold: true,
              color: COR_PRIMARIA,
              letterSpacing: 1,
            },
            {
              text: 'Por Representante — Detalhamento por Pedido e Item',
              fontSize: 9,
              color: COR_SUBTEXTO,
              margin: [0, 2, 0, 0],
            },
          ],
        },
        // Lado direito: período
        {
          stack: [
            {
              text: 'Período de apuração',
              fontSize: 7,
              color: COR_SUBTEXTO,
              alignment: 'right',
            },
            {
              text: `${fmtData(periodo.startDate)}  ->  ${fmtData(periodo.endDate)}`,
              fontSize: 10,
              bold: true,
              color: COR_SECUNDARIA,
              alignment: 'right',
            },
            {
              text: `Emitido em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`,
              fontSize: 7,
              color: COR_SUBTEXTO,
              alignment: 'right',
              margin: [0, 2, 0, 0],
            },
          ],
          width: 200,
        },
      ],
      margin: [0, 0, 0, 10],
    },
    // Linha separadora
    {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 0.5,
          lineColor: COR_ACENTO,
        },
      ],
      margin: [0, 0, 0, 14],
    },
  ]

  // ─── Card do representante ─────────────────────────────────────────────────
  const cardRepresentante = {
    table: {
      widths: ['*'],
      body: [
        [
          {
            stack: [
              {
                columns: [
                  {
                    stack: [
                      {
                        text: 'REPRESENTANTE',
                        fontSize: 7,
                        bold: true,
                        color: COR_ACENTO,
                        margin: [0, 0, 0, 2],
                      },
                      {
                        text: representante.pes_razao,
                        fontSize: 13,
                        bold: true,
                        color: COR_BRANCO,
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        text: 'CPF / CNPJ',
                        fontSize: 7,
                        bold: true,
                        color: COR_ACENTO,
                        alignment: 'right',
                        margin: [0, 0, 0, 2],
                      },
                      {
                        text: representante.pes_cnpj_cpf || '—',
                        fontSize: 11,
                        color: COR_BRANCO,
                        alignment: 'right',
                      },
                    ],
                    width: 160,
                  },
                  {
                    stack: [
                      {
                        text: 'CÓD.',
                        fontSize: 7,
                        bold: true,
                        color: COR_ACENTO,
                        alignment: 'right',
                        margin: [0, 0, 0, 2],
                      },
                      {
                        text: String(representante.cod_pessoa),
                        fontSize: 11,
                        color: COR_BRANCO,
                        alignment: 'right',
                      },
                    ],
                    width: 60,
                  },
                ],
              },
            ],
            fillColor: COR_PRIMARIA,
            margin: [12, 10, 12, 10],
          },
        ],
      ],
    },
    layout: {
      defaultBorder: false,
      hLineColor: () => COR_PRIMARIA,
      vLineColor: () => COR_PRIMARIA,
    },
    margin: [0, 0, 0, 14],
  }

  // ─── Cards de resumo ───────────────────────────────────────────────────────
  const makeResumoCard = (label, valor, bg, cor) => ({
    table: {
      widths: ['*'],
      body: [
        [
          {
            stack: [
              {
                text: label,
                fontSize: 7,
                bold: true,
                color: cor,
                margin: [0, 0, 0, 4],
              },
              { text: valor, fontSize: 13, bold: true, color: cor },
            ],
            fillColor: bg,
            margin: [10, 8, 10, 8],
          },
        ],
      ],
    },
    layout: { defaultBorder: false },
  })

  const resumoCards = {
    columns: [
      makeResumoCard(
        'TOTAL DE PEDIDOS',
        String(comissoes.length),
        '#E3F2FD',
        '#0D47A1',
      ),
      { width: 8, text: '' },
      makeResumoCard('TOTAL VENDIDO', fmt(totalGeral), '#E8EAF6', COR_PRIMARIA),
      { width: 8, text: '' },
      makeResumoCard(
        'TOTAL DE COMISSÃO',
        fmt(comissaoGeral),
        COR_VERDE_BG,
        COR_VERDE,
      ),
      { width: 8, text: '' },
      makeResumoCard(
        '% MÉDIO COMISSÃO',
        fmtPct(pctMedio),
        '#FFF8E1',
        '#E65100',
      ),
    ],
    margin: [0, 0, 0, 18],
  }

  // ─── Blocos de pedido ──────────────────────────────────────────────────────
  const blocosPedidos = []

  comissoes.forEach((comissao, idx) => {
    const items = comissao.products || []

    // Cabeçalho do pedido
    blocosPedidos.push({
      table: {
        widths: ['*'],
        body: [
          [
            {
              columns: [
                {
                  text: `Pedido #${comissao.pedido}`,
                  fontSize: 10,
                  bold: true,
                  color: COR_BRANCO,
                },
                {
                  stack: [
                    {
                      text: comissao.cliente,
                      fontSize: 9,
                      color: '#C5CAE9',
                      alignment: 'right',
                    },
                  ],
                  alignment: 'right',
                },
              ],
              fillColor: COR_SECUNDARIA,
              margin: [10, 7, 10, 7],
            },
          ],
        ],
      },
      layout: { defaultBorder: false },
      margin: [0, idx === 0 ? 0 : 14, 0, 0],
    })

    // Linha com data / total / comissão do pedido
    blocosPedidos.push({
      table: {
        widths: [80, '*', 120, 120],
        body: [
          [
            {
              text: 'Data',
              fontSize: 7,
              bold: true,
              color: COR_SUBTEXTO,
              fillColor: COR_FUNDO_TH,
              margin: [6, 4, 0, 4],
            },
            {
              text: 'Cliente',
              fontSize: 7,
              bold: true,
              color: COR_SUBTEXTO,
              fillColor: COR_FUNDO_TH,
              margin: [4, 4, 0, 4],
            },
            {
              text: 'Total Pedido (R$)',
              fontSize: 7,
              bold: true,
              color: COR_SUBTEXTO,
              fillColor: COR_FUNDO_TH,
              alignment: 'right',
              margin: [0, 4, 6, 4],
            },
            {
              text: 'Comissão (R$)',
              fontSize: 7,
              bold: true,
              color: COR_SUBTEXTO,
              fillColor: COR_FUNDO_TH,
              alignment: 'right',
              margin: [0, 4, 6, 4],
            },
          ],
          [
            {
              text: fmtData(comissao.data),
              fontSize: 9,
              color: COR_TEXTO,
              margin: [6, 5, 0, 5],
            },
            {
              text: comissao.cliente,
              fontSize: 9,
              color: COR_TEXTO,
              margin: [4, 5, 0, 5],
            },
            {
              text: fmt(comissao.total_pedido),
              fontSize: 9,
              bold: true,
              color: COR_TEXTO,
              alignment: 'right',
              margin: [0, 5, 6, 5],
            },
            {
              text: fmt(comissao.comissao),
              fontSize: 9,
              bold: true,
              color: COR_VERDE,
              alignment: 'right',
              margin: [0, 5, 6, 5],
            },
          ],
        ],
      },
      layout: {
        defaultBorder: false,
        hLineColor: () => '#E0E0E0',
        hLineWidth: (i) => (i === 1 ? 0.5 : 0),
      },
      margin: [0, 0, 0, 0],
    })

    // Tabela de itens (se existir)
    if (items.length > 0) {
      const headerItens = [
        {
          text: 'SKU',
          fontSize: 7,
          bold: true,
          color: COR_SUBTEXTO,
          fillColor: COR_FUNDO_TH2,
          margin: [6, 4, 0, 4],
        },
        {
          text: 'Descrição',
          fontSize: 7,
          bold: true,
          color: COR_SUBTEXTO,
          fillColor: COR_FUNDO_TH2,
          margin: [4, 4, 0, 4],
        },
        {
          text: 'Qtd',
          fontSize: 7,
          bold: true,
          color: COR_SUBTEXTO,
          fillColor: COR_FUNDO_TH2,
          alignment: 'center',
          margin: [0, 4, 0, 4],
        },
        {
          text: 'Vl. Unit.',
          fontSize: 7,
          bold: true,
          color: COR_SUBTEXTO,
          fillColor: COR_FUNDO_TH2,
          alignment: 'right',
          margin: [0, 4, 0, 4],
        },
        {
          text: 'Vl. Total',
          fontSize: 7,
          bold: true,
          color: COR_SUBTEXTO,
          fillColor: COR_FUNDO_TH2,
          alignment: 'right',
          margin: [0, 4, 0, 4],
        },
        {
          text: 'Com. %',
          fontSize: 7,
          bold: true,
          color: COR_SUBTEXTO,
          fillColor: COR_FUNDO_TH2,
          alignment: 'right',
          margin: [0, 4, 0, 4],
        },
        {
          text: 'Total Com.',
          fontSize: 7,
          bold: true,
          color: COR_SUBTEXTO,
          fillColor: COR_FUNDO_TH2,
          alignment: 'right',
          margin: [0, 4, 6, 4],
        },
      ]

      const rowsItens = items.map((item, i) => {
        const bg = i % 2 === 0 ? COR_BRANCO : '#FAFAFA'
        return [
          {
            text: item.codigo,
            fontSize: 8,
            color: COR_TEXTO,
            fillColor: bg,
            margin: [6, 4, 0, 4],
          },
          {
            text: item.descricao,
            fontSize: 8,
            color: COR_TEXTO,
            fillColor: bg,
            margin: [4, 4, 0, 4],
          },
          {
            text: String(item.quantidade_nova),
            fontSize: 8,
            color: COR_TEXTO,
            fillColor: bg,
            alignment: 'center',
            margin: [0, 4, 0, 4],
          },
          {
            text: fmt(item.valorunitario),
            fontSize: 8,
            color: COR_TEXTO,
            fillColor: bg,
            alignment: 'right',
            margin: [0, 4, 0, 4],
          },
          {
            text: fmt(item.valor_total_item),
            fontSize: 8,
            color: COR_TEXTO,
            fillColor: bg,
            alignment: 'right',
            margin: [0, 4, 0, 4],
          },
          {
            text: fmtPct(item.porc_comissao),
            fontSize: 8,
            color: '#E65100',
            fillColor: bg,
            alignment: 'right',
            margin: [0, 4, 0, 4],
          },
          {
            text: fmt(item.total_comissao),
            fontSize: 8,
            bold: true,
            color: COR_VERDE,
            fillColor: bg,
            alignment: 'right',
            margin: [0, 4, 6, 4],
          },
        ]
      })

      // Linha de subtotal dos itens
      const subtotalItens = items.reduce(
        (s, i) => s + Number(i.total_comissao),
        0,
      )
      const rowSubtotal = [
        {
          text: '',
          colSpan: 5,
          border: [false, true, false, false],
          borderColor: ['', '#BDBDBD', '', ''],
        },
        {},
        {},
        {},
        {},
        {
          text: 'Subtotal comissão:',
          fontSize: 8,
          bold: true,
          color: COR_SUBTEXTO,
          alignment: 'right',
          border: [false, true, false, false],
          borderColor: ['', '#BDBDBD', '', ''],
          margin: [0, 5, 0, 5],
        },
        {
          text: fmt(subtotalItens),
          fontSize: 8,
          bold: true,
          color: COR_VERDE,
          alignment: 'right',
          border: [false, true, false, false],
          borderColor: ['', '#BDBDBD', '', ''],
          margin: [0, 5, 6, 5],
        },
      ]

      blocosPedidos.push({
        table: {
          widths: [55, '*', 30, 65, 65, 45, 65],
          body: [headerItens, ...rowsItens, rowSubtotal],
        },
        layout: {
          defaultBorder: false,
          hLineColor: () => '#EEEEEE',
          hLineWidth: (i, node) =>
            i === 0 || i === node.table.body.length - 1 ? 0.5 : 0.3,
        },
        margin: [0, 0, 0, 0],
      })
    } else {
      blocosPedidos.push({
        text: 'Nenhum item encontrado para este pedido.',
        fontSize: 8,
        color: COR_SUBTEXTO,
        italics: true,
        margin: [6, 4, 0, 4],
      })
    }

    // Linha final do pedido
    blocosPedidos.push({
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 0.3,
          lineColor: '#CFD8DC',
        },
      ],
      margin: [0, 6, 0, 0],
    })
  })

  // ─── Rodapé de totais gerais ───────────────────────────────────────────────
  const rodapeTotais = {
    table: {
      widths: ['*', 120, 120],
      body: [
        [
          { text: '', border: [false, false, false, false] },
          {
            text: 'TOTAL GERAL VENDIDO',
            fontSize: 8,
            bold: true,
            color: COR_BRANCO,
            alignment: 'right',
            fillColor: COR_PRIMARIA,
            margin: [0, 8, 8, 8],
          },
          {
            text: fmt(totalGeral),
            fontSize: 10,
            bold: true,
            color: COR_BRANCO,
            alignment: 'right',
            fillColor: COR_PRIMARIA,
            margin: [0, 7, 10, 7],
          },
        ],
        [
          { text: '', border: [false, false, false, false] },
          {
            text: 'TOTAL GERAL COMISSÃO',
            fontSize: 9,
            bold: true,
            color: COR_BRANCO,
            alignment: 'right',
            fillColor: COR_VERDE,
            margin: [0, 9, 8, 9],
          },
          {
            text: fmt(comissaoGeral),
            fontSize: 12,
            bold: true,
            color: COR_BRANCO,
            alignment: 'right',
            fillColor: COR_VERDE,
            margin: [0, 8, 10, 8],
          },
        ],
      ],
    },
    layout: {
      defaultBorder: false,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
    margin: [0, 20, 0, 0],
  }

  // ─── Definição do documento ────────────────────────────────────────────────
  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [30, 30, 30, 50],

    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `${representante.pes_razao}  |  Relatório de Comissões`,
          fontSize: 7,
          color: COR_SUBTEXTO,
          margin: [30, 0, 0, 0],
        },
        {
          text: `Página ${currentPage} de ${pageCount}`,
          fontSize: 7,
          color: COR_SUBTEXTO,
          alignment: 'right',
          margin: [0, 0, 30, 0],
        },
      ],
      margin: [0, 10, 0, 0],
    }),

    content: [
      ...header,
      cardRepresentante,
      resumoCards,
      // Seção: detalhamento
      {
        text: 'DETALHAMENTO DOS PEDIDO',
        fontSize: 8,
        bold: true,
        color: COR_PRIMARIA,
        letterSpacing: 1,
        margin: [0, 0, 0, 8],
      },
      ...blocosPedidos,
      rodapeTotais,
    ],

    defaultStyle: {
      font: 'Roboto',
      fontSize: 9,
      color: COR_TEXTO,
    },
  }

  // ─── Abre / baixa o PDF ────────────────────────────────────────────────────

  await pdfMake.createPdf(docDefinition).open()
}
